import { WarningCircle, CopySimple, Circle } from '@phosphor-icons/react';
import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format, formatDistanceToNowStrict, isFuture, isSameDay } from 'date-fns';
import { Button, Modal, notify } from '../../../components';
import CopyToClipboard from 'react-copy-to-clipboard';
import { initiateRefund, getBookingRefundInfo, getBookingsList } from '../api';
import './style';




const getDateRange = (start, end) => {
  if (!start || !end) return '';

  if (isSameDay(start, end)) return format(start, 'dd MMM yyyy');

  return `${format(start, 'dd MMM yy')} - ${format(end, 'dd MMM yy')}`;
};

const getTimeRange = (start, end) => {
  if (!start || !end) return '';

  return `${format(start, 'hh:mm aa')} - ${format(end, 'hh:mm aa')}`;
};

// Helper function to format booking date for display
const formatBookingDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date format in formatBookingDate:', dateString);
      return 'Invalid Date';
    }

    return format(date, 'dd MMM yyyy, hh:mm a');
  } catch (error) {
    console.error('Error formatting booking date:', error);
    return 'Invalid Date';
  }
};

const BookingListing = (props) => {
  const {
    statusTags,
    code,
    onListingClick,
    onRefundClick,
    refundStatus,
    refunds,
    event_details: eventDetails,
    ticket_details: ticket,
    tickets_booked: totalTickets,
    booking_date: bookingDate,
  } = props;

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hide, setHide] = useState(false);

  const parseMaybeArrayOrEpoch = (value) => {
    if (!value) return null;

    if (Array.isArray(value)) {
      const [y, m, d, h = 0, min = 0, s = 0] = value;
      // assume backend gave UTC array → convert to local
      return new Date(Date.UTC(y, m - 1, d, h, min, s));
    }

    if (typeof value === 'number') {
      // if epoch timestamp
      return new Date(value);
    }

    if (typeof value === 'string') {
      // handle ISO string or DB timestamp
      return new Date(value);
    }

    return null;
  };


  function isPastDate(dateArray) {
    if (!Array.isArray(dateArray)) {
      return false;
    }

    const rescheduleTime = parseMaybeArrayOrEpoch(dateArray);

    // const resheduledHour = rescheduleTime.getHours();

    const bookingDateObj = new Date(bookingDate);

    // const year1 = bookingDateObj.getFullYear();
    // const month1 = bookingDateObj.getMonth() + 1; // 👈 JS months are 0-indexed!
    // const day1 = bookingDateObj.getDate();
    // const bookinghour = bookingDateObj.getHours();
    // const minute1 = bookingDateObj.getMinutes();
    // const second1 = bookingDateObj.getSeconds();

    const checking = rescheduleTime > bookingDateObj;

    return checking;
  }



  const ticketsDetails = useMemo(() => {
    if (eventDetails?.smp_detail_entity_set?.length) {
      const config = {};
      eventDetails?.smp_detail_entity_set?.forEach((ticket) => {
        config[ticket?.id] = ticket;
      });

      return config;
    }

    return {};
  }, [eventDetails?.smp_detail_entity_set]);

  const [refundInfo, setRefundInfo] = useState(null);

  useEffect(() => {
    const fetchRefundInfo = async () => {
      if (props?.ticket_status === 'REFUNDED') {
        try {
          getBookingRefundInfo(props?.ticket_details[0]?.smp_id, (response) => {
            if (response) {
              const matchingRefund = response.find(
                refund => refund?.ref_payment_id === props?.pg_payment_id
              );
              if (matchingRefund) {
                setRefundInfo({ data: matchingRefund });
              }
            }
          })
        }
        catch (error) {
          console.error('Error fetching refund info:', error);
        }
      }
    };
    fetchRefundInfo();
  }, [props?.ticket_status, props?.ticket_details, props?.payments]);
  // const isRescheduled = eventDetails?.is_reschedule;  
  // const isCancelled = statusTags?.label?.toLowerCase().includes('cancelled') ||  eventDetails?.is_delete || eventDetails?.status == 'CANCELLED';

  const isCancelled = eventDetails?.status === 'CANCELLED'
    || eventDetails?.is_delete
    || statusTags?.label?.toLowerCase()?.includes('cancelled');

  const isRescheduled = eventDetails?.is_reschedule === true
    && eventDetails?.status !== 'CANCELLED';
  // prevent showing rescheduled if already cancelled

  const shouldShowRefundForRescheduled = () => {
    if (!isRescheduled || !eventDetails?.start_date || !eventDetails?.reschedule_time) {
      return false;
    }

    let eventStartTime = new Date(eventDetails.start_date);
    let rescheduleTime;
    let currentTime = new Date();
    if (Array.isArray(eventDetails.reschedule_time)) {
      const [year, month, day, hour, minute, second] = eventDetails.reschedule_time;
      rescheduleTime = new Date(year, month - 1, day, hour, minute, second);
    } else {
      rescheduleTime = new Date(eventDetails.reschedule_time);
    }

    const hoursUntilEvent = (eventStartTime - currentTime) / (1000 * 60 * 60);
    const hoursFromRescheduleToEvent = (eventStartTime - rescheduleTime) / (1000 * 60 * 60);

    const isRescheduledWithin24HoursOfEvent = hoursFromRescheduleToEvent <= 24;

    // If current time is within 24 hours of the event, no refund
    if (!isRescheduledWithin24HoursOfEvent && hoursUntilEvent <= 24) {
      return false;
    }

    // If rescheduling was done within 24 hours of the event, refund only available up to 3 hours before
    if (isRescheduledWithin24HoursOfEvent && hoursUntilEvent <= 3) {
      return false;
    }

    return true;
  };


  const handleCopy = (link) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRefund = (smp_sales_id, smp_id) => {
    initiateRefund(
      smp_sales_id,
      smp_id,
      (success) => {
        console.log(success);
        setRefundInfo(success);
        getBookingsList();  // Refresh bookings list to reflect refund status
        notify('success', 'Refund process initiated successfully');
        setShowRefundModal(false);
        setHide(true);
      },
      (failure) => {
        console.log(failure)
        notify('error', 'Failed to initiate refund process');
        if (failure?.description === 'booksInvoiceId is null') {
          notify('error', 'Refund failed , please contact support if this continues');
        }
      }
    );
  };


  const getStatusClassName = (statusLabel) => {
    switch (statusLabel) {
      case 'CANCELLED':
        return 'bg-[#D92D201A] text-error';
      case 'BOOKED':
        return 'bg-[#E5FFF6] text-primary-green';
      case 'PAST EVENT':
        return 'bg-grey-10 text-grey-70';
      case 'EVENT CANCELLED':
        return 'bg-[#D92D201A] text-error';
      case 'REFUND INITIATED':
        return 'bg-[#E5FFF6] text-primary-green';
      case 'REFUND SUCCESFUL':
        return 'bg-[#E5FFF6] text-primary-green';
      default:
        return 'bg-[#E5FFF6] text-primary-green';
    }
  };

  const statusClassName = getStatusClassName(statusTags?.label);
  const statusClassName2 = isRescheduled ? 'bg-[#E5FFF6] text-primary-green' : eventDetails?.status == 'CANCELLED' ? 'bg-[#D92D201A] text-error' : null;


  // 🧱 Helper Components
  const RefundStatus = () => (
    <div
      className={`${props?.ticket_status === 'REFUNDED'
        ? 'bg-[#E5FFF6] text-primary-green'
        : statusClassName
        } px-2 py-1 rounded-2xl text-12 flex items-center w-fit`}
    >
      {props?.ticket_status !== 'REFUNDED'
        ? statusTags.label
        : refundInfo?.data?.refund_properties?.status === 'succeeded'
          ? 'REFUND SUCCEEDED'
          : 'REFUND INITIATED'}
    </div>
  );

  const BookedStatus = () => (
    <div
      className={`${statusTags?.label === 'PAST EVENT'
        ? 'bg-grey-10 text-grey-70'
        : 'bg-[#E5FFF6] text-primary-green'
        } px-2 py-1 rounded-2xl text-12 flex items-center w-fit`}
    >
      {statusTags?.label === 'PAST EVENT' ? 'PAST EVENT' : 'BOOKED'}
    </div>
  );

  const RefundButton = () => (
    <div className='flex flex-col gap-2 w-1/2'>
      <Button
        type="primary"
        style={{ width: '161px', height: '56px', marginTop: '8px', whiteSpace: 'nowrap' }}
        onClick={(e) => {
          e.stopPropagation();
          setShowRefundModal(true);
        }}
      >
        Get Refund
      </Button>
    </div>
  );

  return (
    <>
      <div className='relative bg-[#FBEAE9] rounded-[20px] cursor-pointer' onClick={onListingClick}>
        <div className='p-5 pb-3 rounded-2xl shadow-xs block top-[48px] z-20 bg-white'>
          <div className='flex flex-col md:flex-row justify-between'>
            <div className={`${window.innerWidth < 650 ? 'w-full' : 'w-fit'} flex flex-col gap-2 items-start border-b border-dashed border-grey-10 pb-5`}>
              <div className='heading-16'>{props?.event_name}</div>
              <div className='!leading-[18px] bg-light text-primary rounded-2xl label-12 px-3 py-1 w-fit'>
                {`${totalTickets} x ${props?.ticket_details[0]?.category}`}
              </div>
            </div>

            {(isCancelled || (isRescheduled && isPastDate(eventDetails?.reschedule_time))) && (
              <div
                className="h-fit self-start mt-1 rounded-full text-12 font-medium px-3 py-[6px]"
                style={{
                  backgroundColor: isCancelled ? '#D92D201A' : '#E5FFF6',
                  color: isCancelled ? '#D92D20' : '#23AB37'
                }}
              >
                {isCancelled ? 'CANCELLED' : 'EVENT RESCHEDULED'}
              </div>
            )}

          </div>

          <div className='flex justify-between border-b border-dashed border-grey-10'>
            <div className='flex flex-col gap-3 pt-5 pb-7'>
              <div>
                <div className='text-12 text-grey-70 mb-1'>Date</div>
                <div className='label-14'>{getDateRange(eventDetails?.start_date, eventDetails?.end_date)}</div>
              </div>
              <div>
                <div className='text-12 text-grey-70 mb-1'>Time</div>
                <div className='label-14'>{getTimeRange(eventDetails?.start_date, eventDetails?.end_date)}</div>
              </div>
              {eventDetails?.type === 'offline' ? (
                <div>
                  <div className='text-12 text-grey-70 mb-1'>Venue</div>
                  <div className='label-14'>{eventDetails?.event_address?.name}</div>
                </div>
              ) : (
                <div>
                  <div className='text-12 text-grey-70 mb-1'>Where</div>
                  <div className='label-14'>Online</div>
                </div>
              )}
            </div>
            {eventDetails?.type === 'offline' && (
              <div className='flex items-center justify-center'>
                <img src={props?.blobUrl} width={120} height={120} />
              </div>
            )}
          </div>

          <div className='flex justify-between pt-4'>
            <div className='flex flex-col gap-2'>
              {/* {refundInfo ? (
                <div className={`${props?.ticket_status === 'REFUNDED' ? 'bg-[#E5FFF6] text-primary-green' : statusClassName} px-2 py-1 rounded-2xl text-12 flex items-center w-fit`}>
                  {props?.ticket_status !== 'REFUNDED' ? statusTags.label : refundInfo?.data?.refund_properties?.status === 'succeeded' ? 'REFUND SUCCEEDED' : 'REFUND INITIATED'}
                </div>
              ) : ((!isCancelled && props?.ticket_status !== 'REFUNDED' && hide === false) && (!(isRescheduled && props?.ticket_status !== 'REFUNDED' && hide === false && props?.total_price != 0 && !isPastDate(eventDetails?.reschedule_time) && shouldShowRefundForRescheduled())) && (
                <div className={`${statusTags?.label === 'PAST EVENT' ? 'bg-grey-10 text-grey-70' : 'bg-[#E5FFF6] text-primary-green'} px-2 py-1 rounded-2xl text-12 flex items-center w-fit`}>
                  {statusTags?.label === 'PAST EVENT' ? 'PAST EVENT': 'BOOKED'}
                </div>
              )
              )}
              {(isRescheduled && props?.ticket_status !== 'REFUNDED' && hide === false && props?.total_price != 0 && isPastDate(eventDetails?.reschedule_time) && shouldShowRefundForRescheduled()) && (
                <div className='flex flex-col gap-2 w-1/2'>
                 <Button
                   type="primary"
                   style={{ width: '161px', height: '56px', marginTop: '8px', whiteSpace: 'nowrap' }}
                   onClick={(e) => {
                     e.stopPropagation();
                     setShowRefundModal(true);
                   }}
                 >
                   Get Refund
                 </Button>
               </div>
              )} */}

              {refundInfo ? (
                <RefundStatus />
              ) : (
                !isCancelled &&
                !hide &&
                props?.ticket_status !== 'REFUNDED' && (
                  isRescheduled && props?.total_price > 0 && isPastDate(eventDetails?.reschedule_time) && shouldShowRefundForRescheduled()
                    ? <RefundButton />
                    : <BookedStatus />
                )
              )}

              {refundInfo && (
                <div className="flex items-center gap-1">
                  <span className="text-14 text-grey-70">ARN ID:</span>
                  <span className="text-14 text-grey-70">{refundInfo?.data?.refund_properties?.network_reference_number}</span>
                  <span>
                    <CopyToClipboard
                      onCopy={() => handleCopy(`${refundInfo?.data?.refund_properties?.network_reference_number}`)}
                      text={refundInfo?.data?.refund_properties?.network_reference_number}
                    >
                      <span
                        onClick={e => e.stopPropagation()}
                      >
                        <CopySimple
                          size={20}
                          color={copied ? "grey" : "black"}
                          className="cursor-pointer"
                        />
                      </span>
                    </CopyToClipboard>
                  </span>
                </div>
              )}
            </div>
            {props?.bookingId && (
              <div className='flex justify-between pt-4'>
                <div className='text-12 flex items-center'>
                  CODE:
                  <div className='label-12'>{props?.bookingId}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showRefundModal && (
          <>
            <Modal
              size="regular"
              className="modal-refund-confirmation"
              primaryText="Confirm Refund"
              secondaryText="Cancel"
              onClickPrimaryCta={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRefund(props?.payments[0]?.smpSalesId, props?.ticket_details?.[0]?.smp_id);
              }}
              onClickSecondaryCta={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowRefundModal(false);
              }}
              isOpen={showRefundModal}
              isLoading={isRefunding}
            >
              <div className="flex flex-col gap-8">
                <div className="w-14 h-14 flex flex-col justify-center items-center gap-6 warning-bg">
                  <WarningCircle size={32} color='#D92D20' />
                </div>

                <div className='flex flex-col gap-2'>
                  <h1 className='heading-24'>Confirm Refund</h1>
                  <p>Are you sure you want to get a refund for this rescheduled event?</p>
                </div>
              </div>
            </Modal>
          </>
        )}
      </div>


    </>
  );
};

export default BookingListing;